import { useEffect, useState } from 'react';
import { exportApi, organizationApi } from '../services/api';
import { Organization } from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  CardHeader,
  Icon,
  MessageStrip
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

type ExportScope = 'full' | 'processing' | 'service-providers' | 'tom' | 'privacy-notices';

interface ScopeMeta {
  key: ExportScope;
  title: string;
  description: string;
  icon: string;
  run: (orgId: number) => Promise<{ success: boolean; data?: any; error?: string }>;
}

const scopes: ScopeMeta[] = [
  {
    key: 'full',
    title: 'Kompletter Tenant-Export',
    description: 'Alle Module inklusive Relationen. Kann direkt im Import-Menü wieder eingespielt werden.',
    icon: 'database',
    run: (orgId) => exportApi.exportFull(orgId)
  },
  {
    key: 'processing',
    title: 'Verarbeitungen',
    description: 'Nur Verzeichnis der Verarbeitungstätigkeiten (VVT).',
    icon: 'documents',
    run: (orgId) => exportApi.exportProcessing(orgId)
  },
  {
    key: 'service-providers',
    title: 'Dienstleister',
    description: 'Dienstleister inklusive AVV-Verträge.',
    icon: 'supplier',
    run: (orgId) => exportApi.exportServiceProviders(orgId)
  },
  {
    key: 'tom',
    title: 'TOM-Maßnahmen',
    description: 'Technische und organisatorische Maßnahmen.',
    icon: 'shield',
    run: (orgId) => exportApi.exportTom(orgId)
  },
  {
    key: 'privacy-notices',
    title: 'Datenschutzhinweise',
    description: 'Hinweise inklusive Versionen.',
    icon: 'hint',
    run: (orgId) => exportApi.exportPrivacyNotices(orgId)
  }
];

export default function ExportPage() {
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loadingScope, setLoadingScope] = useState<ExportScope | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastExport, setLastExport] = useState<{ scope: ExportScope; filename: string } | null>(null);

  useEffect(() => {
    (async () => {
      const res = await organizationApi.getAll();
      if (res.success && res.data && res.data.length > 0) {
        setCurrentOrg(res.data[0]);
      }
    })();
  }, []);

  const downloadJson = (data: any, filename: string) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = async (scope: ScopeMeta) => {
    if (!currentOrg) return;
    setError(null);
    setLoadingScope(scope.key);

    const res = await scope.run(currentOrg.id);
    if (res.success && res.data) {
      const date = new Date().toISOString().slice(0, 10);
      const orgSlug = currentOrg.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const filename = `dsgvo-buddy-${scope.key}-${orgSlug}-${date}.json`;
      downloadJson(res.data, filename);
      setLastExport({ scope: scope.key, filename });
    } else {
      setError(res.error || 'Export fehlgeschlagen');
    }
    setLoadingScope(null);
  };

  return (
    <div className="page-layout--form">
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>JSON-Export</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Daten als JSON-Datei exportieren. Das Format ist kompatibel zum Import-Menü und eignet sich
            für Backups, Migrationen und den Transfer zwischen Umgebungen.
          </Text>
        </div>
      </FlexBox>

      {error && (
        <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
          {error}
        </MessageStrip>
      )}

      {lastExport && (
        <MessageStrip design="Positive" style={{ marginBottom: '1rem' }}>
          Export „{lastExport.filename}" erfolgreich heruntergeladen.
        </MessageStrip>
      )}

      <FlexBox direction="Column" style={{ gap: '1rem' }}>
        {scopes.map(scope => (
          <Card
            key={scope.key}
            header={
              <CardHeader
                titleText={scope.title}
                subtitleText={scope.description}
                avatar={<Icon name={scope.icon} />}
                action={
                  <Button
                    icon="download"
                    design="Emphasized"
                    onClick={() => handleExport(scope)}
                    disabled={!currentOrg || loadingScope !== null}
                  >
                    {loadingScope === scope.key ? 'Wird erstellt...' : 'JSON herunterladen'}
                  </Button>
                }
              />
            }
          />
        ))}
      </FlexBox>

      <MessageStrip design="Information" hideCloseButton style={{ marginTop: '1.5rem' }}>
        Hinweis: Der Export enthält Klartextdaten. Bewahren Sie die Dateien sicher auf und teilen Sie sie
        nur mit autorisierten Personen. Der kompletter Tenant-Export kann über das Import-Menü wieder
        eingespielt werden (Schema-Version 1.0).
      </MessageStrip>
    </div>
  );
}
